import { FormatterContext } from "#Contexts/FormatterContext"
import NumberHelpers from "#Utils/number_helpers"

const UNSET_VALUES = [null, undefined]
const useFormattedNumber = ({ removeSpaces } = {}) => {
  const formatter = React.useContext(FormatterContext)

  const getOptions = (number, precision) => {
    const numberFormat = formatter.number
    const separator = numberFormat === "DC" ? "," : "."
    const delimiter = numberFormat === "DC" ? "." : ","

    const options = {
      separator,
      delimiter,
      strip_empty_fractional_parts: true,
      strip_insignificant_zeros: true,
      precision: precision,
      significant: false,
      labels: {
        thousand: "K",
        million: "M",
        billion: "B",
        trillion: "T",
        quadrillion: "Q",
      },
    }

    return options
  }

  const formatNumberWithPrecision = (number, precision) => {
    const options = getOptions(number, precision)
    if (UNSET_VALUES.includes(number)) number = 0
    const formattedNumber = NumberHelpers.number_with_precision(number, options)

    return removeSpaces ? formattedNumber.replace(/\s/g, "") : formattedNumber
  }

  // Rounds to the given number of significant digits, so 0.00003123 keeps its digits
  // instead of collapsing to 0 and 123456.7 does not grow decimals.
  const formatNumberWithSignificantDigits = (number, digits) => {
    if (UNSET_VALUES.includes(number)) number = 0
    const rounded = Number(number.toPrecision(digits))
    const decimals =
      rounded === 0
        ? 0
        : Math.min(100, Math.max(0, digits - 1 - Math.floor(Math.log10(Math.abs(rounded)))))
    const [integer, fraction = ""] = rounded.toFixed(decimals).split(".")
    const { separator, delimiter } = getOptions(number)
    const whole = NumberHelpers.number_with_delimiter(integer, { delimiter })
    const trimmed = fraction.replace(/0+$/, "")
    const formattedNumber = trimmed === "" ? whole : `${whole}${separator}${trimmed}`

    return removeSpaces ? formattedNumber.replace(/\s/g, "") : formattedNumber
  }
  const formatNumberToHuman = (number, precision) => {
    const options = getOptions(number, precision)
    if (UNSET_VALUES.includes(number)) number = 0
    const formattedNumber = NumberHelpers.number_to_human(number, options)

    return removeSpaces ? formattedNumber.replace(/\s/g, "") : formattedNumber
  }

  return { formatNumberWithPrecision, formatNumberWithSignificantDigits, formatNumberToHuman }
}

useFormattedNumber.propTypes = {
  number: PropTypes.number.isRequired,
  precision: PropTypes.number,
}

export default useFormattedNumber
